<template>
  <ThemeProvider>
    <div class="main-wrapper">
      <div
        class="flex wrapper"
        :style="{
          height: `calc(100% - 64px)`,
        }"
      >
        <div v-if="userId" class="sider" :class="siderClass">
          <div class="relative h-full">
            <XSider />
          </div>
        </div>
        <div
          :key="userId"
          class="content"
          :style="{
            backgroundImage: `url(${transformImage(getFeUrl(`/youyan-platform/png/white.png`))})`,
            ...contentStyle,
          }"
          @scroll="scrolling"
        >
          <ClientOnly>
            <Compatibility />
          </ClientOnly>
          <slot></slot>
        </div>
      </div>
      <div v-if="!isLoginPage" ref="topRef">
        <TopHeader @show-user-pop="handleShowUserPop"></TopHeader>
      </div>
    </div>
    <Account />
    <VideoDownload />
    <ClientOnly>
      <TemplateModal />
    </ClientOnly>
    <component :is="mobileBottomUserPopComponent" ref="mobileUserRef" />
    <PublishProcess />
    <TasteAccountHint v-model:open="showCreateModal" :accounts="accountList" />
    <FloatFeatures class="pc-only" v-if="route.name !== PageKey.LAB" />
  </ThemeProvider>
</template>

<script lang="ts" setup>
import ThemeProvider from '@/design/widgets/theme-provider/index.vue';

import Compatibility from '~/components/compatibility/index.vue';
import { PageKey } from '~/constants/page';
import MobileBottomUserPop from '~/design/widgets/mobile-bottom-user-pop/index.vue';
import MobileBottomUserPopEn from '~/design/widgets/mobile-bottom-user-pop-en/index.vue';
import TopHeader from '~/design/widgets/top-header/index.vue';
import { useDetector } from '~/hooks/useDetector';

import Account from '@/features/account/index.vue';
import PublishProcess from '@/features/publish/index.vue';
import VideoDownload from '@/features/videoDownload/index.vue';
import FloatFeatures from '@rendora/common/features/float-features/index.vue';
import TasteAccountHint from '~/features/tasteAccountHint/index.vue';
import TemplateModal from '~/features/template/components/modal.vue';

import { transformImage } from '~/utils/';
import { getFeUrl } from '~/utils/url';

const route = useRoute();
const router = useRouter();
const userInfo = useUserInfo();
const shareVisible = useShare();
const { isHome } = useDetector();
const { isMobile } = useDevice();
const { createMobileUserPopOwner, registerMobileUserPop } = useMobileUserPop();
const showCreateModal = useCheckCreate();
const accountList = useAccountList();
const topRef = ref();
const mobileUserRef = ref<{ open: () => void } | null>(null);
const mobileUserPopOwnerId = createMobileUserPopOwner();

const topHeight = ref(0);

useResizeObserver(topRef, entries => {
  const entry = entries[0];
  const { height } = entry.contentRect;
  topHeight.value = height + 65;
});

const routeType = computed(() => route.fullPath.split('/')[1]);
const isLoginPage = computed(() => route.name == PageKey.LOGIN);

const userId = computed(() => userInfo.value?.user_id);
const mobileBottomUserPopComponent = computed(() =>
  getEnv('area') === 'CN' ? MobileBottomUserPop : MobileBottomUserPopEn,
);

const siderClass = computed(() => {
  return getEnv('area') === 'CN' ? 'w-[240px]' : 'w-[258px]';
});

const contentStyle = computed(() => {
  if (userId.value) {
    return {
      width: `calc(100vw - ${getEnv('area') === 'CN' ? 240 : 258}px)`,
    };
  }
  return {
    width: '100vw',
  };
});

const checkDataPermission = () => {
  const routeList = router.getRoutes();
  const currentRoute = route.name;
  const targetRouter = routeList.find(item => item.name === currentRoute);
  const result = checkRoutePermission(targetRouter);

  if (!result) {
    router.push({
      name: PageKey.CENTER_ACCOUNT,
    });
  }
};

const scrolling = () => {
  if (!shareVisible.value) {
    return;
  }

  shareVisible.value = false;
};

const handleShowUserPop = () => {
  if (isMobile.value) {
    mobileUserRef.value?.open();
  }
};

watch(
  mobileUserRef,
  value => {
    registerMobileUserPop(mobileUserPopOwnerId, value ? () => value.open() : null);
  },
  { immediate: true },
);

onBeforeUnmount(() => {
  registerMobileUserPop(mobileUserPopOwnerId, null);
});
// 个人中心页面退出登录跳转到首页精选
watch(
  () => userInfo.value?.user_id,
  (current, before) => {
    if (routeType.value === 'center') {
      if (current) {
        checkDataPermission();
      } else {
        goHome();
      }
      return;
    }

    // home内容若用户退出登录需定位到首页
    if (current !== before && !current && isHome.value) {
      goHome();
    }
  },
  {
    immediate: true,
  },
);
</script>

<style lang="scss" scoped>
.wrapper {
  @apply bg-back-bg absolute w-full;

  .sider {
    background-color: var(--menu-bg);
    flex-shrink: 0;
  }

  .content {
    @apply flex flex-col w-full;
    height: 100%;
    border-left: 1px solid var(--back-2);
    background: var(--back-bg);
    :deep(> :last-child) {
      @apply flex flex-col;
      flex: 1;
      overflow: auto;
    }
  }
}
</style>
